// @flow
import type { Context, Node } from 'react';
import React, { createContext, useContext } from 'react';
import { margin } from '@styled-system/space';
import classnames from 'classnames';
import styled, { type StyledComponent } from 'styled-components';

import type { MarginSystemProps } from '@rover/kibble/components/styledSystemPropTypes';
import {
  ButtonColor,
  Color,
  DSTokenMap,
  FONT_FAMILY_SEMIBOLD,
  FontSize,
  FontWeight,
  Spacing,
} from '@rover/kibble/styles';

import { type ButtonAppearance, type Props as CommonProps, ButtonType } from './Button.types';

export type Props = {
  ...CommonProps,
  ...MarginSystemProps,
  buttonType: $PropertyType<CommonProps, 'buttonType'>,
  className: string,
  type: 'button' | 'submit',
  role?: string,
  tabIndex?: number,
  // eslint's require-default-props rule doesn't seem to play well with hyphens.
  /* eslint-disable react/require-default-props */
  'aria-label'?: string,
  'data-qa-id'?: string,
  /* eslint-enable react/require-default-props */
};

export type TextProps = {|
  children: Node,
|};

type ButtonTypeToColor = { [ButtonType]: ButtonColor };

const buttonTypeToTextColor: ButtonTypeToColor = {
  [ButtonType.DEFAULT]: ButtonColor.BUTTON_DEFAULT_COLOR,
  [ButtonType.PRIMARY]: DSTokenMap.BACKGROUND_COLOR_PRIMARY,
  [ButtonType.PRIMARY_ACCESSIBLE]: DSTokenMap.BACKGROUND_COLOR_PRIMARY,
  [ButtonType.DARK]: ButtonColor.BUTTON_DARK_COLOR,
  [ButtonType.TEXT]: DSTokenMap.INTERACTIVE_TEXT_COLOR_LINK_PRIMARY,
  [ButtonType.TEXT_LIGHT]: DSTokenMap.INTERACTIVE_TEXT_COLOR_LINK_PRIMARY_INVERSE,
  [ButtonType.TERTIARY]: ButtonColor.BUTTON_TERTIARY_COLOR,
};

const StyledButton: StyledComponent<Props> = styled.button`
  display: inline-block;
  margin-bottom: 0;
  font-family: ${FONT_FAMILY_SEMIBOLD};
  font-weight: ${FontWeight.BOLD};
  -webkit-font-smoothing: antialiased;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  white-space: nowrap;
  padding: ${Spacing.S} ${Spacing.M};
  border: 1px solid transparent;
  font-size: ${FontSize.M};
  line-height: 1.6;
  border-radius: ${DSTokenMap.BORDER_RADIUS_SECONDARY};
  user-select: none;
  ${margin}

  &:disabled {
    cursor: not-allowed;
  }

  &.${ButtonType.DEFAULT} {
    background: ${ButtonColor.BUTTON_DEFAULT_BACKGROUND};
    border-color: ${ButtonColor.BUTTON_DEFAULT_BORDER};
    color: ${buttonTypeToTextColor[ButtonType.DEFAULT]};

    &:hover {
      background: ${ButtonColor.BUTTON_DEFAULT_HOVER};
    }
  }

  &.${ButtonType.PRIMARY} {
    background: ${DSTokenMap.GRADIENT_PRIMARY};
    color: ${buttonTypeToTextColor[ButtonType.PRIMARY]};

    &:hover {
      background: ${DSTokenMap.GRADIENT_PRIMARY_PRESSED};
    }
  }

  &.${ButtonType.PRIMARY_ACCESSIBLE} {
    background: ${Color.NEUTRAL_28};
    border-color: ${Color.NEUTRAL_28};
    color: ${buttonTypeToTextColor[ButtonType.PRIMARY_ACCESSIBLE]};

    &:hover {
      background: ${Color.NEUTRAL_28};
      border-color: ${Color.NEUTRAL_28};
    }
  }

  &.${ButtonType.DARK} {
    background: ${ButtonColor.BUTTON_DARK_BACKGROUND};
    border-color: ${ButtonColor.BUTTON_DARK_BORDER};
    color: ${buttonTypeToTextColor[ButtonType.DARK]};

    &:hover {
      background: ${ButtonColor.BUTTON_DARK_HOVER};
    }
  }

  &.${ButtonType.TEXT}, &.${ButtonType.TEXT_LIGHT} {
    border: none;
    display: inline;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
    &:not(:focus) {
      outline-offset: 0;
      outline: none;
    }
  }

  &.${ButtonType.TEXT} {
    background: ${ButtonColor.BUTTON_TEXT_BACKGROUND};
    color: ${buttonTypeToTextColor[ButtonType.TEXT]};

    &:hover {
      color: ${DSTokenMap.INTERACTIVE_TEXT_COLOR_LINK_PRIMARY_PRESSED};
    }
  }

  &.${ButtonType.TEXT_LIGHT} {
    background: ${ButtonColor.BUTTON_TEXT_LIGHT_BACKGROUND};
    color: ${buttonTypeToTextColor[ButtonType.TEXT_LIGHT]};
  }

  &.${ButtonType.DANGER} {
    background: ${Color.RED_40};
  }

  &.${ButtonType.TERTIARY} {
    color: ${buttonTypeToTextColor[ButtonType.TERTIARY]};
    background: ${ButtonColor.BUTTON_TERTIARY_BACKGROUND};

    &:hover {
      background: ${ButtonColor.BUTTON_TERTIARY_BACKGROUND_HOVER};
    }
    &:active {
      background: ${ButtonColor.BUTTON_TERTIARY_BACKGROUND_PRESSED};
    }
  }
`;

type StyledTextProps = {
  buttonType: $PropertyType<Props, 'buttonType'>,
};

const getTextColor = ({ buttonType }) => buttonTypeToTextColor[buttonType] || 'black';

const StyledText: StyledComponent<StyledTextProps> = styled.span`
  color: ${getTextColor};
  font-weight: ${FontWeight.NORMAL};
`;

const ButtonContext: Context<ButtonAppearance | void> = createContext();

const Button = ({ className, buttonType, children, ...other }: Props): Node => {
  const buttonAppearance: ButtonAppearance = { buttonType };
  const finalClassName = classnames(className, buttonType.toString());

  return (
    <StyledButton className={finalClassName} {...other} buttonType={buttonType}>
      {/* $FlowFixMe children is defined in common but flow misses it */}
      <ButtonContext.Provider value={buttonAppearance}>{children}</ButtonContext.Provider>
    </StyledButton>
  );
};

const Text = ({ children }: TextProps): Node => {
  const buttonAppearance = useContext(ButtonContext);
  const buttonType = buttonAppearance ? buttonAppearance.buttonType : ButtonType.DEFAULT;
  return <StyledText buttonType={buttonType}>{children}</StyledText>;
};

Button.Text = Text;

Button.defaultProps = {
  type: 'button',
  className: '',
  buttonType: ButtonType.DEFAULT,
  'aria-label': undefined,
  role: undefined,
  tabIndex: undefined,
  disabled: undefined,
  'data-qa-id': undefined,
};

Button.types = ButtonType;

export default Button;



// WEBPACK FOOTER //
// ../react-lib/src/components/buttons/Button/Button.jsx